import {Component, Input, Output, EventEmitter, OnInit} from '@angular/core';
import {EventResponse, EventClassResponse, EventServiceResponse, CompanyResponse, EventExpenseResponse, TrainingPackageResponse} from '../../../../../../../../@shared/models/response.module';
import {formatTimeDMYHHMM} from '../../../../../../../../@core/utils/utils.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EditEvent} from './components/edit-event/edit-event';
import {EditEventImages} from './components/edit-event-images/edit-event-images';
import {EventsService} from '../../../../../../../../@shared/services/events-service';
import {TrainingService} from '../../../../../../../../@shared/services/training.service';
import {AQH_EVE_6, AQH_EVE_11} from '../../../../../../../../@shared/models/input.module';

@Component({
  selector: 'app-manage-event-overview',
  standalone: false,
  templateUrl: './manage-event-overview.html',
  styleUrl: './manage-event-overview.css'
})
export class ManageEventOverview implements OnInit {
  @Input() event: EventResponse;
  @Input() company: CompanyResponse | null | undefined;
  @Output() eventUpdated = new EventEmitter<void>();

  eventClasses: EventClassResponse[] = [];
  eventServices: EventServiceResponse[] = [];
  eventExpenses: EventExpenseResponse[] = [];
  trainingPackages: TrainingPackageResponse[] = [];

  protected readonly formatTimeDMYHHMM = formatTimeDMYHHMM;

  get isSocialEvent(): boolean {
    return this.event?.category === 'social';
  }

  get isTrainingEvent(): boolean {
    return this.event?.category === 'training';
  }

  get isCompetitionEvent(): boolean {
    return this.event?.category === 'competition' || !this.event?.category;
  }

  constructor(private modalService: NgbModal,
              private eventService: EventsService,
              private trainingService: TrainingService) {
  }

  ngOnInit() {
    this.loadEventData();
  }

  loadEventData() {
    this.getEventClasses();
    this.getEventServices();
    this.getEventExpenses();
    this.getTrainingPackages();
  }

  getEventClasses() {
    let params: AQH_EVE_6 = {
      event_id: this.event.event_id
    };
    this.eventService.getEventClasses(params).subscribe((data) => {
      this.eventClasses = data;
    });
  }

  getEventServices() {
    let params: AQH_EVE_11 = {
      event_id: this.event.event_id
    };
    this.eventService.getEventServices(params).subscribe((data) => {
      this.eventServices = data;
    });
  }

  getEventExpenses() {
    this.eventService.getEventExpenses(this.event.event_id).subscribe((data) => {
      this.eventExpenses = data;
    });
  }

  getTrainingPackages() {
    this.trainingService.getPackages({ event_id: this.event.event_id }).subscribe((data) => {
      this.trainingPackages = data;
    });
  }

  getTotalClassRegistrations(): number {
    return this.eventClasses.reduce((total, eventClass) => {
      return total + (eventClass.event_class_registrations?.length || 0);
    }, 0);
  }

  getTotalClassRevenue(): number {
    return this.eventClasses.reduce((total, eventClass) => {
      const registrations = eventClass.event_class_registrations?.length || 0;
      const price = eventClass.price || 0;
      return total + (registrations * price);
    }, 0);
  }

  getTotalServiceRegistrations(): number {
    return this.eventServices.reduce((total, service) => {
      const serviceRegs = service.event_service_registrations?.reduce((sum, reg) => {
        return sum + (reg.num_registrations || 0);
      }, 0) || 0;
      return total + serviceRegs;
    }, 0);
  }

  getTotalServiceRevenue(): number {
    return this.eventServices.reduce((total, service) => {
      const serviceRegs = service.event_service_registrations?.reduce((sum, reg) => {
        return sum + (reg.num_registrations || 0);
      }, 0) || 0;
      const price = service.price || 0;
      return total + (serviceRegs * price);
    }, 0);
  }

  getTotalPackageRegistrations(): number {
    return this.trainingPackages.reduce((total, pkg) => {
      return total + (pkg.current_registrations || 0);
    }, 0);
  }

  getTotalPackageRevenue(): number {
    return this.trainingPackages.reduce((total, pkg) => {
      const registrations = pkg.current_registrations || 0;
      const price = (pkg.price_cents || 0) / 100;
      return total + (registrations * price);
    }, 0);
  }

  getTotalRevenue(): number {
    return this.getTotalClassRevenue() + this.getTotalServiceRevenue() + this.getTotalPackageRevenue();
  }

  getTotalExpenses(): number {
    return this.eventExpenses.reduce((total, expense) => {
      return total + (expense.amount || 0);
    }, 0);
  }

  getNetEarnings(): number {
    return this.getTotalRevenue() - this.getTotalExpenses();
  }

  getDefaultCurrency(): string {
    return this.event?.default_currency || 'NOK';
  }

  getServiceRegistrationCount(service: EventServiceResponse): number {
    return service.event_service_registrations?.reduce((sum, reg) => {
      return sum + (reg.num_registrations || 0);
    }, 0) || 0;
  }

  getServiceRevenue(service: EventServiceResponse): number {
    const regs = this.getServiceRegistrationCount(service);
    return regs * (service.price || 0);
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00 per transaction
   */
  calculateNetAmount(grossAmount: number, transactionCount: number): number {
    if (!grossAmount || grossAmount <= 0) return 0;
    // Apply fee per transaction: (gross × 0.956) - (2.00 × transactions)
    const net = grossAmount * 0.956 - (2 * transactionCount);
    return Math.max(0, Math.round(net * 100) / 100);
  }

  getTotalNetClassRevenue(): number {
    return this.eventClasses.reduce((total, eventClass) => {
      const registrations = eventClass.event_class_registrations?.length || 0;
      const price = eventClass.price || 0;
      const gross = registrations * price;
      return total + this.calculateNetAmount(gross, registrations);
    }, 0);
  }

  getTotalNetServiceRevenue(): number {
    return this.eventServices.reduce((total, service) => {
      const serviceRegs = service.event_service_registrations?.reduce((sum, reg) => {
        return sum + (reg.num_registrations || 0);
      }, 0) || 0;
      const transactionCount = service.event_service_registrations?.length || 0;
      const price = service.price || 0;
      const gross = serviceRegs * price;
      return total + this.calculateNetAmount(gross, transactionCount);
    }, 0);
  }

  getTotalNetPackageRevenue(): number {
    return this.trainingPackages.reduce((total, pkg) => {
      const registrations = pkg.current_registrations || 0;
      const price = (pkg.price_cents || 0) / 100;
      const gross = registrations * price;
      return total + this.calculateNetAmount(gross, registrations);
    }, 0);
  }

  getTotalNetRevenue(): number {
    return this.getTotalNetClassRevenue() + this.getTotalNetServiceRevenue() + this.getTotalNetPackageRevenue();
  }

  getNetEarningsAfterFees(): number {
    return this.getTotalNetRevenue() - this.getTotalExpenses();
  }

  getClassNetRevenue(eventClass: EventClassResponse): number {
    const registrations = eventClass.event_class_registrations?.length || 0;
    const price = eventClass.price || 0;
    const gross = registrations * price;
    return this.calculateNetAmount(gross, registrations);
  }

  getServiceNetRevenue(service: EventServiceResponse): number {
    const gross = this.getServiceRevenue(service);
    const transactionCount = service.event_service_registrations?.length || 0;
    return this.calculateNetAmount(gross, transactionCount);
  }

  getPackageNetRevenue(pkg: TrainingPackageResponse): number {
    const registrations = pkg.current_registrations || 0;
    const price = (pkg.price_cents || 0) / 100;
    const gross = registrations * price;
    return this.calculateNetAmount(gross, registrations);
  }

  openEditEventModal() {
    let modalRef = this.modalService.open(EditEvent, {centered: true, size: 'xl'});
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((result) => {
      if (result) {
        this.eventUpdated.emit();
      }
    }).catch(() => {});
  }

  openEditImagesModal() {
    let modalRef = this.modalService.open(EditEventImages, {centered: true, size: 'lg'});
    modalRef.componentInstance.event = this.event;
    modalRef.result.then((result) => {
      if (result) {
        this.eventUpdated.emit();
      }
    }).catch(() => {});
  }
}
